// Parse a Markdown workout/program document (the reverse of lib/exportDoc.js) and match its
// exercise names against the exercise library. Pure functions, no imports — unit-tested in
// test/importDoc.test.ts. Tolerant of hand-written / LLM-authored docs that follow the same
// shape: `## Day N: Title` days, `### 1. Exercise Name` blocks, `- Target/Equipment/Rest/Notes`
// bullets. Weights are converted to kg here; matching returns library rows untouched.

const LB_PER_KG = 2.2046226218;

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

// "2:30" / "1:02:00" → seconds (null if not a plain clock time).
function parseClock(s) {
  const m = /^(?:(\d+):)?(\d+):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  return (m[1] ? +m[1] * 3600 : 0) + +m[2] * 60 + +m[3];
}

// The "- Target: …" prescription line → structured targets. Handles the exporter's shapes
// ("3 × 8–12 reps @ 20 kg", "5 km in 30:00 (6:00/km)", "8 reps (bodyweight)") plus common
// hand-written variants ("3 x 8-12 reps per leg (note)", "2 × 10 min").
export function parseTarget(text) {
  const out = { notes: [] };
  let t = String(text || "").trim();

  const mSets = /^(\d+)\s*[×x]\s*/i.exec(t);
  if (mSets) { out.target_sets = +mSets[1]; t = t.slice(mSets[0].length); }

  const mW = /@\s*([\d.,]+)\s*(kg|lbs?)\b/i.exec(t);
  if (mW) {
    const v = parseFloat(mW[1].replace(",", "."));
    if (!isNaN(v)) out.target_weight_kg = /lb/i.test(mW[2]) ? Math.round((v / LB_PER_KG) * 100) / 100 : v;
  }

  const mR = /(\d+)\s*(?:[–—-]|to)\s*(\d+)\s*reps?\b/i.exec(t) || /(\d+)\s*reps?\b/i.exec(t);
  if (mR) {
    out.target_reps_min = +mR[1];
    out.target_reps_max = mR[2] != null ? +mR[2] : +mR[1];
  }

  // Duration: first clock time not followed by "/" (that would be a pace like "6:00/km").
  const mD = /(?:(\d+):)?(\d+):(\d{2})(?!\s*\/|\d)/.exec(t);
  if (mD) out.target_duration_seconds = (mD[1] ? +mD[1] * 3600 : 0) + +mD[2] * 60 + +mD[3];
  else {
    const mMin = /(\d+(?:[.,]\d+)?)\s*min(?:ute)?s?\b/i.exec(t);
    if (mMin) out.target_duration_seconds = Math.round(parseFloat(mMin[1].replace(",", ".")) * 60);
  }

  const mDist = /([\d.,]+)\s*(km|mi|miles?|meters?|m)\b/i.exec(t);
  if (mDist) {
    const v = parseFloat(mDist[1].replace(",", "."));
    if (!isNaN(v)) {
      const u = mDist[2].toLowerCase();
      out.target_distance_m = u.startsWith("k") ? Math.round(v * 1000) : u.startsWith("mi") ? Math.round(v * 1609.344) : Math.round(v);
    }
  }

  // "per leg/arm/side" and any trailing parenthetical that isn't a marker/pace → keep as a note
  // so the prescription's nuance survives the round-trip.
  const mPer = /\bper\s+(leg|arm|side|hand)\b/i.exec(t);
  if (mPer) out.notes.push(`per ${mPer[1].toLowerCase()}`);
  const mParen = /\(([^)]*)\)\s*$/.exec(t);
  if (mParen) {
    const inner = mParen[1].trim();
    const isMarker = /^bodyweight$/i.test(inner) || /^\d+:\d{2}\s*\/\s*(km|mi)$/i.test(inner);
    if (inner && !isMarker) out.notes.push(inner);
  }
  return out;
}

// Split an exercise heading into name + superset group ("Curl _(superset A1)_" → group 1).
function splitSuperset(raw) {
  const m = /\s*[_*]*\(\s*superset\s+([A-Za-z])(\d+)?\s*\)[_*]*\s*$/i.exec(raw);
  if (!m) return { name: raw.trim(), superset_group: null };
  return { name: raw.slice(0, m.index).trim(), superset_group: m[1].toUpperCase().charCodeAt(0) - 64 };
}

// "Glutes (secondary: Hamstrings, Lower Back)" → { primary: [...], secondary: [...] } (hint only).
function parseMuscles(text) {
  const m = /^(.*?)(?:\(\s*secondary:\s*([^)]*)\))?\s*$/i.exec(String(text || ""));
  const split = (s) => (s || "").split(/[,;·]/).map((x) => x.trim()).filter(Boolean);
  return { primary: split(m[1]), secondary: split(m[2]) };
}

const stripMd = (s) => s.replace(/^[_*]+|[_*]+$/g, "").trim();

/**
 * Parse a Markdown document into an import plan.
 * Returns { kind: 'program'|'workout', name, description, notes, days, exercises } where
 * program days are [{ title, label, workout_name, is_rest, exercises }] and each exercise is
 * { name, superset_group, target_sets, target_reps_min/max, target_weight_kg,
 *   target_duration_seconds, target_distance_m, rest_seconds, notes, equipment, muscles }.
 * Returns null when nothing recognizable was found.
 */
export function parseDocument(text) {
  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const doc = { name: null, description: null, notes: null, days: [], exercises: [] };
  let day = null;      // current program day
  let ex = null;       // current exercise block
  let sawStructure = false;

  const exercisesSink = () => (day ? day.exercises : doc.exercises);

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;

    const h1 = /^#\s+(.*)$/.exec(line);
    if (h1 && !doc.name) { doc.name = stripMd(h1[1]); continue; }

    const dayM = /^##\s+Day\s+(\d+)\s*:\s*(.*)$/i.exec(line);
    if (dayM) {
      sawStructure = true;
      ex = null;
      const title = stripMd(dayM[2]);
      const restM = /^(.*?)\s*\(\s*rest(?:\s+day)?\s*\)\s*$/i.exec(title);
      let label = null, workoutName = null;
      if (!restM) {
        // Exporter writes "Label — WorkoutName"; a bare title is the workout name itself.
        const parts = title.split(/\s+—\s+/);
        if (parts.length === 2) { label = parts[0].trim(); workoutName = parts[1].trim(); }
        else workoutName = title;
      }
      day = {
        title,
        is_rest: !!restM,
        label: restM ? (restM[1].trim() || "Rest") : label,
        workout_name: workoutName,
        exercises: [],
      };
      doc.days.push(day);
      continue;
    }

    const exM = /^#{2,4}\s+(\d+)\s*[.)]\s+(.*)$/.exec(line);
    if (exM) {
      sawStructure = true;
      const { name, superset_group } = splitSuperset(stripMd(exM[2]));
      ex = {
        name, superset_group,
        target_sets: null, target_reps_min: null, target_reps_max: null, target_weight_kg: null,
        target_duration_seconds: null, target_distance_m: null, rest_seconds: null,
        notes: null, equipment: null, muscles: null,
      };
      exercisesSink().push(ex);
      continue;
    }

    const bullet = /^-\s*(Target|Equipment|Muscles|Rest|Notes)\s*:\s*(.*)$/i.exec(line);
    if (bullet && ex) {
      const key = bullet[1].toLowerCase();
      const val = bullet[2].trim();
      if (key === "target") {
        const t = parseTarget(val);
        for (const k of ["target_sets", "target_reps_min", "target_reps_max", "target_weight_kg", "target_duration_seconds", "target_distance_m"]) {
          if (t[k] != null) ex[k] = t[k];
        }
        if (t.notes.length) ex.notes = [ex.notes, t.notes.join("; ")].filter(Boolean).join("; ");
      } else if (key === "rest") {
        const secs = parseClock(val) ?? parseTarget(val).target_duration_seconds ?? null;
        if (secs != null) ex.rest_seconds = secs;
      } else if (key === "notes") {
        ex.notes = [ex.notes, val].filter(Boolean).join("; ");
      } else if (key === "equipment") {
        ex.equipment = val;
      } else if (key === "muscles") {
        ex.muscles = parseMuscles(val);
      }
      continue;
    }

    // Workout-doc notes line ("_Notes: …_") and the first plain paragraph → description.
    if (!day && !ex) {
      const noteM = /^_?Notes?:\s*(.*?)_?$/i.exec(line);
      if (noteM && doc.name && !doc.notes) { doc.notes = stripMd(noteM[1]); continue; }
      const isNoise = /^([#>*-]|\d+\.|\*\*|_Exported|\||```)/.test(line);
      if (doc.name && !doc.description && !isNoise) doc.description = stripMd(line);
    }
  }

  // Days with a workout title but no parsed exercises are effectively rest slots.
  for (const d of doc.days) {
    if (!d.is_rest && !d.exercises.length) { d.is_rest = true; d.label = d.label || d.workout_name || "Rest"; d.workout_name = null; }
  }

  if (!sawStructure) return null;
  doc.kind = doc.days.length ? "program" : "workout";
  if (!doc.name) doc.name = doc.kind === "program" ? "Imported program" : "Imported workout";
  return doc;
}

// ---------------------------------------------------------------------------
// Library matching
// ---------------------------------------------------------------------------

// Compound words that appear both joined and split ("Triceps Pushdown" vs "push down").
const COMPOUNDS = [
  ["push down", "pushdown"], ["pull down", "pulldown"], ["pull up", "pullup"],
  ["push up", "pushup"], ["chin up", "chinup"], ["step up", "stepup"], ["sit up", "situp"],
  ["kick back", "kickback"], ["dead lift", "deadlift"],
];
const STOPWORDS = new Set(["with", "the", "a", "an", "using", "on", "off", "of"]);
const SYNONYMS = { db: "dumbbell", bb: "barbell", kb: "kettlebell", dumbell: "dumbbell", "e-z": "ez" };

// Words that make two different exercises, not two names for the same one. If the query names
// one member of a group and the candidate names a DIFFERENT member, the pair can't auto-match
// (a suggestion at most) — this is what keeps "Incline Press" off "Decline Press" and
// "Hip Abduction" off "Hip Adduction".
const EXCLUSIVE = [
  ["incline", "decline"],
  ["abduction", "adduction", "abductor", "adductor"],
  ["seated", "standing", "lying", "kneeling"],
  ["dumbbell", "barbell", "cable", "kettlebell", "smith", "band"],
  ["overhand", "underhand"],
  ["front", "rear", "reverse"],
];

function singular(t) {
  return t.length > 3 && t.endsWith("s") && !t.endsWith("ss") ? t.slice(0, -1) : t;
}

function tokensOf(name) {
  let s = String(name || "").toLowerCase().replace(/[’']/g, "").replace(/[^a-z0-9]+/g, " ").trim();
  for (const [split, joined] of COMPOUNDS) s = s.replace(split, joined);
  const out = new Set();
  for (let t of s.split(/\s+/)) {
    if (!t || STOPWORDS.has(t)) continue;
    t = singular(t);
    out.add(SYNONYMS[t] || t);
  }
  return out;
}

const keyOf = (tokens) => [...tokens].sort().join(" ");

function dice(a, b) {
  if (!a.size || !b.size) return 0;
  let inter = 0;
  for (const t of a) if (b.has(t)) inter++;
  return (2 * inter) / (a.size + b.size);
}

// True when the two token sets name DIFFERENT members of an exclusive group.
function conflicts(a, b) {
  for (const group of EXCLUSIVE) {
    let inA = null, inB = null;
    for (const w of group) {
      if (a.has(w)) inA = inA ?? w;
      if (b.has(w)) inB = inB ?? w;
    }
    if (inA && inB && inA !== inB) return true;
  }
  return false;
}

/**
 * Precompute match variants for the library (call once per import). Each library row gets its
 * full name plus parenthesized aliases ("Hyperextensions (Back Extensions)" is findable under
 * both) as token sets.
 */
export function indexLibrary(library) {
  return (library || []).map((row) => {
    const name = String(row.exercise_name || "");
    const variants = [name];
    const noParen = name.replace(/\([^)]*\)/g, " ").trim();
    if (noParen && noParen !== name) variants.push(noParen);
    for (const m of name.matchAll(/\(([^)]+)\)/g)) variants.push(m[1]);
    const seen = new Set();
    const tokenSets = [];
    for (const v of variants) {
      const t = tokensOf(v);
      const k = keyOf(t);
      if (t.size && !seen.has(k)) { seen.add(k); tokenSets.push(t); }
    }
    return { row, tokenSets, equipment: String(row.equipment || "").toLowerCase() };
  });
}

const AUTO_THRESHOLD = 0.66;
const SUGGEST_THRESHOLD = 0.34;

/**
 * Match one document exercise name against an indexed library.
 * Returns { match, exact, score, suggestions } — `match` is the library row when the best score
 * clears the auto threshold (exact = verbatim/alias hit), otherwise null with up to 3
 * `suggestions` [{ row, score }] for the user to pick from.
 */
export function matchExercise(index, name, hints = {}) {
  const q = tokensOf(name);
  const qKey = keyOf(q);
  const hintTokens = hints.equipment ? tokensOf(hints.equipment) : null;
  const scored = [];

  for (const entry of index) {
    let best = 0, exact = false;
    for (const t of entry.tokenSets) {
      let s;
      if (keyOf(t) === qKey) { s = 1; exact = true; }
      else {
        s = dice(q, t);
        if (s > 0 && conflicts(q, t)) s = Math.min(s, 0.5);
      }
      if (s > best) best = s;
    }
    if (best <= 0) continue;
    // Equipment agreement nudges ties apart but never fabricates an exact match.
    if (!exact && hintTokens && entry.equipment && hintTokens.has(singular(entry.equipment)) ) best = Math.min(best + 0.05, 0.98);
    scored.push({ row: entry.row, score: best, exact });
  }

  scored.sort((a, b) => b.score - a.score);
  const top = scored[0];
  const match = top && top.score >= AUTO_THRESHOLD ? top : null;
  return {
    match: match ? match.row : null,
    exact: !!(match && match.exact),
    score: top ? top.score : 0,
    suggestions: scored.filter((s) => s.score >= SUGGEST_THRESHOLD).slice(0, 3),
  };
}
